import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit, Output,
  Renderer2
} from '@angular/core';
import {DragulaService} from "ng2-dragula";
import {BuilderHistoryService} from "../../../../builder-history.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {BuilderTourEnum} from "../../../../builder-tour.enum";
import {interval, takeWhile, tap} from "rxjs";
import {BuilderSectionsInteractionService} from "../../../../builder-sections-interaction.service";
import {skip} from "rxjs/internal/operators/skip";
import {BuilderGeneratedPageSectionI, SectionTemplateI} from "../../../../builder-v2.interface";
import {filter} from "rxjs/operators";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {FormControl} from "@angular/forms";
import {BuilderV2Service} from "../../../../builder-v2.service";
import {User} from "@sentry/browser";
import {TriggersService} from "../../../../services/triggers.service";
import {CanvasZoomService} from "../../../../services/canvas-zoom.service";
import {DragHelperService} from "../../../../services/drag-helper.service";
import {CanvasPageInteractionsService} from "../../../../services/canvas-page-interactions.service";
import {CommonBuilderDataService} from "../../../../services/common-builder-data.service";
import {Config} from "../../../../../config/config";
import {CookieService} from "ngx-cookie";
import {VibeCodingFlowService} from "../../../../../shared/services/vibe-coding-flow.service";
import {Website} from "../../../../../websites/website/website";

@Component({
    selector: 'db-page-constructor',
    templateUrl: './page-constructor.component.html',
    styleUrl: './page-constructor.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PageConstructorComponent implements OnInit, OnDestroy {
  @Input() sectionTemplates: SectionTemplateI[] = [];
  @Input() pageId: number;
  tourEnum = BuilderTourEnum;
  pageSections: BuilderGeneratedPageSectionI[] = [];
  staticSections: BuilderGeneratedPageSectionI[] = [];
  draggableSections: BuilderGeneratedPageSectionI[] = [];
  private _destroyRef = inject(DestroyRef);
  selectedSection: BuilderGeneratedPageSectionI;
  selectedSectionIndex: number;
  showActions = false;
  loading = true;
  isEdit = false;
  isDragging: boolean = false;
  disabledSteps = [BuilderTourEnum.PAGE_BUILDER, BuilderTourEnum.START, BuilderTourEnum.INTRO];
  sectionErrors: boolean[] = [];
  sectionWarns: boolean[] = [];
  sectionTemplatesOpened = false;
  changedSectionId: number = null;
  templatesForSection: number = null;
  sectionLoading = false;
  generationType = null;
  readonly sectionsLimit = this._commonDataService.homePageSectionsLimit;
  _triggerService: TriggersService = inject(TriggersService);
  isMobile: boolean = false;
  tempoSection = null;
  vibeCodingFlow: any;
  prefilledAi = this._commonDataService.prefilledAi;
  @Output() openMagicLink: EventEmitter<boolean> = new EventEmitter<boolean>();


  @Input() popupMode: boolean;
  @Input() uniqueId: string;
  @Input() user: User;
  @Input() website: Website;

  @HostListener('document:keydown.control.d', ['$event'])
  @HostListener('document:keydown.meta.d', ['$event'])
  duplicate(event: KeyboardEvent) {
    if (!this.selectedSection) {
      return;
    }
    event.preventDefault();
    this.duplicateSection(this.selectedSectionIndex);
  }

  @HostListener('document:keydown.backspace', ['$event'])
  @HostListener('document:keydown.delete', ['$event'])
  remove(event: KeyboardEvent) {
    if (this._restrictDelete()) return;
    event.preventDefault();
    this.removeSection(this.selectedSectionIndex);
  }

  @HostListener('document:keydown.control.enter', ['$event'])
  @HostListener('document:keydown.meta.enter', ['$event'])
  addSectionAbove(event: KeyboardEvent) {
    if (!this.selectedSection) {
      return;
    }
    event.preventDefault();
    this.addSection(this.selectedSectionIndex + 1);
  }

  constructor(private _dragulaService: DragulaService,
              private _ngZone: NgZone,
              private _cdr: ChangeDetectorRef,
              private _cookieService: CookieService,
              private _renderer: Renderer2,
              public pageInteractionsService: CanvasPageInteractionsService,
              public _sectionsInteractionsService: BuilderSectionsInteractionService,
              private _googleAnalyticsService: GoogleAnalyticsService,
              private _builderV2Service: BuilderV2Service,
              private _canvasZoomService: CanvasZoomService,
              private _dragHelperService: DragHelperService,
              private _commonDataService: CommonBuilderDataService,
              public builderHistoryService: BuilderHistoryService,
              private _vibeCodingFlowService: VibeCodingFlowService) {
    this._dragulaService.createGroup('sections', {
      revertOnSpill: true,
      direction: 'vertical',
      moves: () => {
        return this.builderHistoryService.tourStep.value === this.tourEnum.END;
      }
    });
    this._dragulaService.drag('sections').subscribe(() => {
      this.isDragging = true;
      this.sectionTemplatesOpened = false;
      this.templatesForSection = null;
    });

    this._dragulaService.dragend('sections').subscribe(() => {
      this.isDragging = false;
    });
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 991;
    this.vibeCodingFlow = this._vibeCodingFlowService.isVibeCodingFlow() || this.website?.isWVC;

    this.loadSections();
    this._listenStateChange();
    this._listenTour();
    this._listenOutlineChangesFromTemplate();
    this._listenSectionChange();
    this._listenSectionValueChange();
    this._listenSectionGenerate();

    }

  private _listenSectionGenerate() {
    this.pageInteractionsService.generateSection
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(({pageId, sectionId, section, type}) => {
        if (this.pageId === pageId) {
          const sectionIndex = this.draggableSections.findIndex(section => section.id === sectionId);
          this.onGenerate(sectionIndex, type);
        }
      });
  }

  private _listenSectionValueChange() {
    this.pageInteractionsService.sectionValueChange
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(({pageId, sectionId, section}) => {
        if (this.pageId === pageId) {
          const sectionIndex = this.draggableSections.findIndex(section => section.id === sectionId);
          const changedValues = {
            section_title: section.title_control.value,
            section_description: section.description_control.value
          };
          this.sectionValueChange(changedValues, this.draggableSections[sectionIndex], sectionIndex + 1);
        }
      });
  }

  private _listenOutlineChangesFromTemplate() {
    this._sectionsInteractionsService.changePageOutline
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(value => {
        const {pageId, sectionId} = this.pageInteractionsService.openAddSection.value;
        if (this.pageId === pageId) {
          value.index = this.pageSections.findIndex(section => section.id === sectionId);
          this._handleAddTemplateSection(value);
        }
      });
  }

  private _handleAddTemplateSection({tempo, template, action, ...rest}) {
    const index = rest['index'] !== null ? rest['index'] : this.pageSections.length - 2;
    if (action === 'add') {
      const id = new Date().getTime();
      const elementToAdd = {
        id,
        index,
        section_title: template.ui_name,
        section_description: template.description,
        section_type: template.key,
        is_tempo: tempo,
        loading: !tempo,
        title_control: new FormControl(template.ui_name),
        description_control: new FormControl(template.description),
      };
      if (!tempo) {
        const isCustom = template.key === 'custom';
        this.templatesForSection = null;
        this.tempoSection = null;
        this.addSection(index, !isCustom ? elementToAdd : null, !isCustom);

        if (!isCustom) {
          this.changedSectionId = id;
          this._sectionsInteractionsService.enhanceSection.next({type: 'description', index: index + 1});
        }

        setTimeout(() => {
          this.selectSection(this.draggableSections[index], index, isCustom);
          this._cdr.detectChanges();
        }, 100);
      } else {
        this.tempoSection = elementToAdd;
        this._cdr.detectChanges();
      }
    } else {
      this.tempoSection = null;
      this._cdr.detectChanges();
    }
  }

  private _listenTour() {
    this.builderHistoryService.tourStep
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._selectSectionForTour();
      });
  }

  private _selectSectionForTour() {
    if (this.builderHistoryService.tourStep.value === this.tourEnum.PAGE_BUILDER && !this.selectedSection) {
      if (this.draggableSections[0]) {
        this.selectSection(this.draggableSections[0], 0);
      }
    }
  }

  // TODO refactor
  loadSections() {
    const interval$ = interval(350);
    let addedItemsCount = 0;
    interval$.pipe(
      filter(() => !!this._sectionsInteractionsService.pageSections.length),
      tap(() => {
        if (this.pageSections.length && addedItemsCount >= this._sectionsInteractionsService.pageSections.length) {
          this.loading = false;
          this._setErrors();
          this.builderHistoryService.addStateInHistory(structuredClone({
            sections: this._sectionsInteractionsService.removeFormControls(this.pageSections),
            invalidSections: this.sectionErrors
          }));
          this._sectionsInteractionsService.pageLoaded.next(true);
          this._listenSectionTypesChange();
          this._sectionsInteractionsService.checkAddLimit(this.draggableSections.length, this.sectionsLimit);
          this._cdr.markForCheck();
        }
      }),
      takeWhile(() => this.loading)
    ).subscribe(() => {
      const currentIndex = addedItemsCount;
      const item = this._sectionsInteractionsService.pageSections[currentIndex];
      if (item) {
        item.title_control = new FormControl(item.section_title);
        item.description_control = new FormControl(item.section_description);
        addedItemsCount++;
        this.pageSections.push(item);
        if (currentIndex === 0 || currentIndex === this._sectionsInteractionsService.pageSections.length - 1) {
          this.staticSections.push(item);
          return;
        }

        this.draggableSections.push(item);
        this._selectSectionForTour();
        this._cdr.markForCheck();
      }
    });
  }

  private _setErrors() {
    this.pageSections.map((section: BuilderGeneratedPageSectionI, index) => {
      this.sectionErrors[index - 1] = (!section.section_title)
        && !(!section.section_description && !section.section_title);
      this.sectionWarns[index - 1] = (!section.section_description)
        && !(!section.section_description && !section.section_title);
    });
  }

  private _listenSectionTypesChange() {
    this._sectionsInteractionsService.updateSectionTypes
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(sections => !!sections?.length)
      ).subscribe(sections => {
      for (let section of sections) {
        const changedSection: BuilderGeneratedPageSectionI = this.pageSections.find(sec => sec.section_title === section.section_title &&
          sec.section_description === section.section_description);
        if (changedSection) {
          changedSection.section_type = section.section_type;
          changedSection.section_title = section.section_title;
          changedSection.section_description = section.section_description;
          changedSection.loading = false;
          changedSection.is_tempo = false;

          if (section.variation) {
            changedSection.variation = section.variation;
          } else {
            changedSection.user_preferences = section.user_preferences;
          }
        }
      }
      if (sections?.at(0)?.saveInState) {
        this.builderHistoryService.addInitialState(this._sectionsInteractionsService.removeFormControls(this.pageSections));
      }
      this._sectionsInteractionsService.pageSections = this.pageSections;
      this._sectionsInteractionsService.saveLocalState.next();
    });
  }

  onClickAddSection(index: number, section?) {
    if (!this._sectionsInteractionsService.canAddSection.value || this.loading || this.sectionLoading) {
      return;
    }
    this.sectionTemplatesOpened = false;
    this.templatesForSection = section ? -1 : index;
    const sectionId = section ? section.id : this.draggableSections[index].id;
    this.pageInteractionsService.openAddSection.next({pageId: this.pageId, sectionId});
    this.selectSection(section || this.draggableSections[index], index, false);
  }

  private _listenSectionChange() {
    this._sectionsInteractionsService.updateEnhancedSection
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      ).subscribe(section => {
      const changedSection: BuilderGeneratedPageSectionI = this.pageSections.find(sec => sec.id === this.changedSectionId);
      if (changedSection) {
        changedSection.section_title = section.section_title;
        changedSection.section_description = section.section_description;
        changedSection.loading = false;
        changedSection.is_tempo = false;
        this.sectionLoading = false;
        this.pageInteractionsService.sectionIsGenerating.next(false);
      }

      if (section.index !== 0 && section.index !== this.pageSections.length - 1) {
        const draggableSection = this.draggableSections[section.index - 1];
        draggableSection.loading = false;
        draggableSection.is_tempo = false;
        draggableSection.section_title = section.section_title;
        draggableSection.section_description = section.section_description;
        draggableSection.description_control.setValue(draggableSection.section_description);
        draggableSection.title_control.setValue(draggableSection.section_title);
        this.draggableSections = [...this.draggableSections];
        if (this.pageInteractionsService.openSectionEdit.value?.sectionId !== draggableSection.id) {
          this.pageInteractionsService.openSectionEdit.next({
            pageId: this.pageId,
            sectionId: draggableSection.id,
            section: draggableSection
          });
        }

        this._cdr.markForCheck();
        this._cdr.detectChanges();
      }

      this._sectionsInteractionsService.pageSections = this.pageSections;
      this._sectionsInteractionsService.saveLocalState.next();
      this._setErrors();
      this.updateHistory(this.pageSections);

      if (this.generationType === 'new') {
        const sections = this._sectionsInteractionsService.removeFormControls(this.pageSections);
        this._sectionsInteractionsService.sectionChanges.next({sections, changedIndex: section.index});
      }
      this.changedSectionId = null;
      this.generationType = null;
    });
  }

  private _listenStateChange() {
    this.builderHistoryService.currentStateIndex
      .pipe(
        skip(1),
        takeUntilDestroyed(this._destroyRef),
      ).subscribe(index => {
      const history = this.builderHistoryService.history.value[index];

      if (!this.builderHistoryService.history.value || !history) {
        return;
      }
      const sections = structuredClone(history.sections);
      this.sectionErrors = structuredClone(history.invalidSections);
      this.pageSections = this._sectionsInteractionsService.addFormControls(sections);
      this._sectionsInteractionsService.pageSections = sections;
      this._sectionsInteractionsService.saveLocalState.next();
      this.staticSections = [sections.at(0), sections.at(-1)];
      this.draggableSections = sections.slice(1, -1);
      this._cdr.markForCheck();
    });
  }

  onSectionEdit(value: boolean) {
    this.isEdit = value;
    this._sectionsInteractionsService.isSectionEdit = value;
  }

  addSection(index: number, elementToAdd?, skipAddHistory?: boolean) {
    if (!this._sectionsInteractionsService.canAddSection.value) {
      return;
    }
    if (!elementToAdd) {
      elementToAdd = {
        id: new Date().getTime(),
        index,
        section_title: "",
        section_description: "",
        section_type: null,
        title_control: new FormControl(''),
        description_control: new FormControl(''),
      };
    }
    this.draggableSections.splice(index, 0, elementToAdd);
    this.pageSections.splice(index + 1, 0, elementToAdd);
    this.selectSection(elementToAdd, index);
    this._sectionsInteractionsService.checkAddLimit(this.draggableSections.length, this.sectionsLimit);
    this.updateHistory(this.pageSections, skipAddHistory);
    this._sendEvent((this.vibeCodingFlow ? 'Vibe-coding AI Flow' : 'Section-based AI Flow') + ' - Section Added', '-');
  }

  removeSection(index) {
    this._unsetEditTooltips(this.draggableSections[index].id);
    this.draggableSections.splice(index, 1);
    this.pageSections.splice(index + 1, 1);
    if (this.sectionErrors[index]) {
      this.sectionErrors.splice(index, 1);
    }
    if (this.sectionWarns[index]) {
      this.sectionWarns.splice(index, 1);
    }
    this.updateHistory(this.pageSections);

    // if (this.draggableSections[index]) {
    //   this.selectSection(this.draggableSections[index], index);
    // }
    // if (!this.draggableSections.length) {
    //   this.selectSection(this.staticSections[0], 0, false);
    // }
    this._sectionsInteractionsService.checkAddLimit(this.draggableSections.length, this.sectionsLimit);
    this._setErrors();
    this._sendEvent((this.vibeCodingFlow ? 'Vibe-coding AI Flow' : 'Section-based AI Flow') + ' - Section Deleted', '-');
  }

  private _unsetEditTooltips(sectionId: number) {
    if (this.pageInteractionsService.openSectionEdit.value?.pageId === this.pageId &&
      sectionId === this.pageInteractionsService.openSectionEdit.value?.sectionId) {
      this.pageInteractionsService.openSectionEdit.next(null);
    }

    if (this.pageInteractionsService.openAddSection.value?.pageId === this.pageId &&
      sectionId === this.pageInteractionsService.openAddSection.value?.sectionId) {
      this.pageInteractionsService.openAddSection.next(null);
    }
  }

  duplicateSection(index) {
    const {description_control, title_control, ...exampleSection} = this.pageSections[index + 1];
    const duplicate = structuredClone(exampleSection);
    duplicate.id = new Date().getTime();
    duplicate['title_control'] = new FormControl(duplicate.section_title);
    duplicate['description_control'] = new FormControl(duplicate.section_description);

    this.draggableSections.splice(index + 1, 0, duplicate);
    this.pageSections.splice(index + 2, 0, duplicate);
    this.selectedSection = duplicate;
    this.updateHistory(this.pageSections);
  }

  onGenerate(index: number, type: 'new' | 'enhance' | 'title' | 'description') {
    const section = this.draggableSections[index];
    this._sectionsInteractionsService.pageSections[index + 1] = {
      ...section,
      section_title: section.title_control.value,
      section_description: section.description_control.value
    };
    this.draggableSections[index].loading = true;
    this.sectionLoading = true;
    this.changedSectionId = this.draggableSections[index].id;
    this.pageInteractionsService.sectionIsGenerating.next(true);
    this.generationType = type;
    this._sectionsInteractionsService.enhanceSection.next({type, index: ++index});
    this._cdr.detectChanges();
  }

  updateHistory(data: BuilderGeneratedPageSectionI[], skipAddHistory = false) {
    data = this._sectionsInteractionsService.removeFormControls(data);
    let sections = structuredClone(data);
    const invalidSections = structuredClone(this.sectionErrors);
    const secondaryPages = this._sectionsInteractionsService.secondaryPages.value;
    if (!skipAddHistory) {
      this.builderHistoryService.addStateInHistory({sections, invalidSections, secondaryPages});
    }
    this._sectionsInteractionsService.pageSections = sections;
    this._sectionsInteractionsService.saveLocalState.next();
  }

  sectionValueChange(value: {
                       section_title: string,
                       section_description: string
                     }, section: BuilderGeneratedPageSectionI,
                     index: number, isStatic = false) {
    // section.section_type = null; remove after check
    section = {
      ...section,
      ...value,
      section_type: null
    };
    this.pageSections[index] = section;
    if (!isStatic) {
      this.draggableSections[index - 1] = section;
    }
    const sections = this._sectionsInteractionsService.removeFormControls(this.pageSections);
    this.sectionErrors[index - 1] = (!section.section_title)
      && !(!section.section_description && !section.section_title);
    this.sectionWarns[index - 1] = (!section.section_description)
      && !(!section.section_description && !section.section_title);
    this._sectionsInteractionsService.sectionChanges.next({sections, changedIndex: index});
    this._cdr.detectChanges();
    this.updateHistory(this.pageSections);
  }

  onStructureChange(data) {
    this.draggableSections = data;
    this.pageSections = [this.staticSections[0], ...data, this.staticSections[1]];
    this._setErrors();
    this.updateHistory(this.pageSections);
    this.selectedSection = null;
  }

  checkMobileClick() {
    if (this.isMobile) {
      this.openMagicLink.emit(true);
    }
  }

  rightClick(event, section, index) {
    if (this.isEdit || this.tourEnum.END !== this.builderHistoryService.tourStep.value) {
      return;
    }
    this.showActions = true;
    if (event) {
      event.preventDefault();
      this.selectSection(section, index);
    }
  }

  onCloseRightClick() {
    this.showActions = false;
  }

  selectSection(section, index, openEdit = true) {
    this.selectedSection = section;
    this.selectedSectionIndex = index;

    if (openEdit) {
      this.pageInteractionsService.openSectionEdit.next({pageId: this.pageId, sectionId: section.id, section});
    }
  }


  // TODO fix on scale drag
  dragClassChanged() {
    this._dragHelperService.recalculateDraggableElementPosition(this._canvasZoomService.scale.value, this._renderer);
  }

  resetSelection() {
    if (this.tourEnum.PAGE_BUILDER === this.builderHistoryService.tourStep.value) {
      return;
    }
    this.selectedSection = null;
    this.selectedSectionIndex = null;
    if (this.pageInteractionsService.openSectionEdit.value?.pageId === this.pageId) {
      this.pageInteractionsService.openSectionEdit.next(null);
    }
  }

  private _restrictDelete() {
    const isStaticSection = this.staticSections?.at(0)?.id === this.selectedSection?.id ||
      this.staticSections?.at(1)?.id === this.selectedSection?.id;
    return !this.selectedSection ||
      this.isEdit ||
      isStaticSection;
  }

  private _sendEvent(action: string, label: string): void {
    const analyticsData = {
      eventCategory: this.popupMode ? 'Dashboard Action' : 'Onboarding',
      eventAction: action,
      eventLabel: label,
      uniqueId: this.uniqueId,
      isMobile: this.isMobile
    };
    if (!this.user) {
      this._googleAnalyticsService.sendAnalyticsEventWithCoreWithoutUserData(analyticsData);
    } else {
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    }
  }

  openSectionTemplates() {
    if (window.innerWidth <= 991) {
      this.openMagicLink.emit(true);
      return;
    }
    const isEmpty = !this.draggableSections.length;
    const index = !isEmpty ? this.draggableSections.length - 1 : 0;
    const section = !isEmpty ? this.draggableSections[index] : this.pageSections[index];
    this.pageInteractionsService.openAddSection.next({pageId: this.pageId, sectionId: section.id});
    // this.selectSection(section, index, false);
  }

  ngOnDestroy() {
    this._dragulaService.destroy('sections');
  }

}
